/**
 * Състояние на автентикацията.
 *
 * Правата идват от сървъра и служат само за скриване на недостъпното —
 * същинската проверка е на сървъра (Раздел 9).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Permission } from '@vidanalyser/shared';
import { apiRequest, setAccessToken, setUnauthenticatedHandler } from '../api/client';
import type { CurrentUser } from '../api/types';

interface AuthContextValue {
  user: CurrentUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setUnauthenticatedHandler(() => setUser(null));

    // Опит за възстановяване на сесията от refresh cookie при презареждане.
    const restore = async (): Promise<void> => {
      try {
        const refreshed = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });
        if (!refreshed.ok) return;

        const { accessToken } = (await refreshed.json()) as { accessToken: string };
        setAccessToken(accessToken);
        setUser(await apiRequest<CurrentUser>('/api/auth/me'));
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    void restore();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await apiRequest<{ accessToken: string; user: CurrentUser }>(
      '/api/auth/login',
      { method: 'POST', body: { email, password } },
    );

    setAccessToken(response.accessToken);
    setUser(response.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiRequest('/api/auth/logout', { method: 'POST' });
    } finally {
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      login,
      logout,
      can: (permission) => user?.permissions.includes(permission) ?? false,
    }),
    [user, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth се ползва само в AuthProvider');
  return context;
}
